import { uploadFile } from './upload-file';
import { resolveUploadedLogoUrl } from './resolve-uploaded-logo-url';
import { createDeferredFileUpload } from './deferred-file-upload';

export interface BuildLogoUploadUrlParams {
  adminUrl: string;
  projectId: string;
  env: string;
  uniqueCustomerId: string;
  pypeId: string;
}

/**
 * Build the REST path the backend proxy (`uploadPypeLogo`) expects for a
 * channel logo PUT. Extracted from the old inline `getOnUpload('logo')` URL so
 * it is unit-testable and reused by the deferred Save-time upload.
 */
export const buildLogoUploadUrl = ({
  adminUrl,
  projectId,
  env,
  uniqueCustomerId,
  pypeId,
}: BuildLogoUploadUrlParams): string =>
  `${adminUrl}/rest/pypes/${projectId}/${env}/${uniqueCustomerId}/${pypeId}/logo`;

/** Context fields the logo PUT URL needs; each maps to a validation-message key. */
export type LogoUploadContextField = 'projectId' | 'env' | 'customerId';

/**
 * Validate the context required to build the logo PUT URL. Returns the first
 * missing field (so the caller can surface the matching validation message) or
 * `null` when everything is present. Restores the `env` null-guard that the old
 * inline `getOnUpload('logo')` had — an empty `env` would otherwise produce a
 * malformed `…/pypes/{projectId}//{customerId}/…` path (PSTUDIO-3078 review).
 */
export const validateLogoUploadContext = ({
  projectId,
  env,
  uniqueCustomerId,
}: {
  projectId?: string;
  env?: string;
  uniqueCustomerId?: string;
}): LogoUploadContextField | null => {
  if (!projectId) {
    return 'projectId';
  }
  if (!env) {
    return 'env';
  }
  if (!uniqueCustomerId) {
    return 'customerId';
  }
  return null;
};

/**
 * @deprecated Prefer `createDeferredFileUpload`. Kept as a named alias so the
 * logo call site + PSTUDIO-3078 tests remain stable; identical behavior.
 */
export const createDeferredLogoUpload = createDeferredFileUpload;

/**
 * Perform the real logo PUT now (deferred from file-select to Save). Resolves
 * with the cache-busted logo URL the backend returned (or null if it returned
 * none), rejects with the transport error so the Save handler can surface it
 * and abort navigation.
 */
export const uploadLogoFile = ({
  url,
  file,
}: {
  url: string;
  file: File;
}): Promise<string | null> =>
  new Promise((resolve, reject) => {
    uploadFile({
      url,
      file,
      type: 'logo',
      load: () => undefined,
      onChange: (payload) => resolve(resolveUploadedLogoUrl(payload)),
      error: (e) => reject(e),
    });
  });
